import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

import Seo from '../components/Seo';
import ProductImageCarousel from '../components/ProductImageCarousel';
import VariationSelector from '../components/VariationSelector';
import QuantitySelector from '../components/QuantitySelector';
import Button from '../components/Button';
import ProductPackagingInfo from '../components/ProductPackagingInfo';
import ProductVariationsTable from '../components/ProductVariationsTable';
import ProductSpecs from '../components/ProductSpecs';
import Faq from '../components/Faq';
import ProductCarousel from '../components/ProductCarousel';
import { useCart } from '../context/CartContext';
import useAuth from '../utils/useAuth';
import cleanSubcategoryName from '../utils/cleanSubcategoryName';
import { formatUnits, formatArrivalDate, getArrivals, hasColorBreakdown } from '../utils/arrivals';

import {
  generateWebsiteSchema,
  generateOrganizationSchema,
  generateBreadcrumbSchema,
  generateProductSchema
} from '../utils/schemaGenerators';
import { generateFaqPageSchema } from '../utils/faqSchema';

import '../styles/pages/ProductPage.scss';
import LogoLoader from '../components/LogoLoader';

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL = process.env.REACT_APP_S3_BASE_URL || '';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [productImages, setProductImages] = useState({ thumbnails: [], fullSize: [] });
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [predictive, setPredictive] = useState(null);
  const [variationError, setVariationError] = useState('');
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data: fetchedProduct } = await axios.get(`${API_URL}/products/${slug}`, { headers: { authorization: token } });
        setProduct(fetchedProduct);

        if (fetchedProduct && Array.isArray(fetchedProduct.image_files) && fetchedProduct.image_files.length > 0) {
          const BASE_URL = process.env.REACT_APP_S3_BASE_URL;
          setProductImages({
            thumbnails: fetchedProduct.image_files.map(file => `${BASE_URL}/thumbnails/${file}`),
            fullSize: fetchedProduct.image_files.map(file => `${BASE_URL}/${file}`),
          });
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
        setImagesLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  useEffect(() => {
    if (!product) return;
    const fetchEstimate = async () => {
      try {
        const token = localStorage.getItem('token');
        const params = {
          variation: selectedVariation || '',
          quantity: selectedQuantity,
        };
        const { data } = await axios.get(
          `${API_URL}/products/${slug}/estimate`,
          {
            headers: { authorization: token },
            params,
          }
        );
        setPredictive(data);
      } catch (err) {
        console.error('Error fetching estimate:', err);
      }
    };
    fetchEstimate();
  }, [slug, selectedVariation, selectedQuantity, product]);

  if (loading) return <LogoLoader size="lg" />;
  if (!product) return <div>Product not found</div>;
  if (imagesLoading) return <LogoLoader size="lg" />;

  // La bodega ya no ofrece el producto: se sigue mostrando (enlaces guardados) pero no se cotiza.
  const isUnavailable = product.is_active === false;

  const hasVariations =
    (Array.isArray(product.variations) && product.variations.length > 0) ||
    (!Array.isArray(product.variations) && product.variations && Object.keys(product.variations).length > 0);

  const handleVariationSelect = (variation) => {
    setSelectedVariation(variation);
    setVariationError('');
  };

  const handleQuantityChange = (quantity) => {
    setSelectedQuantity(quantity);
  };

  const handleRequestQuotation = () => {
    if (hasVariations && !selectedVariation) {
      setVariationError('Por favor, selecciona una variación antes de cotizar.');
      return;
    }
    addToCart(product, selectedVariation, selectedQuantity);
  };

  const upcomingArrivals = getArrivals(product);
  const arrivalsByColorAvailable = hasColorBreakdown(upcomingArrivals);

  const scrollToStock = () => {
    document.getElementById('existencias')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  let displayPublicPrice = product.publicPrice;
  if (!displayPublicPrice || displayPublicPrice === 'N/A' || displayPublicPrice === 'n/a') {
    displayPublicPrice = 'Precio no disponible';
  }

  const pageTitle = `${product.title} | Promocionales en Stock`;
  const pageDescription =
    product.description ||
    `${product.title} - Producto promocional personalizable en Colombia.`;
  const canonicalUrl = `/productos/${slug}`;

  const categories = Array.isArray(product.Categories) ? product.Categories : [];
  const subcategories = Array.isArray(product.Subcategories) ? product.Subcategories : [];
  const productCategory = categories[0] || null;
  const productSubcategory =
    subcategories.find((s) => productCategory && s.categoryId === productCategory.id) ||
    subcategories[0] ||
    null;

  const breadcrumbs = [
    { name: 'Inicio', url: '/' },
    { name: 'Categorías', url: '/categorias' },
    productCategory?.slug
      ? { name: productCategory.name, url: `/categorias/${productCategory.slug}` }
      : null,
    productSubcategory?.slug && productCategory?.slug
      ? {
          name: cleanSubcategoryName(productSubcategory.name),
          url: `/categorias/${productCategory.slug}/${productSubcategory.slug}`,
        }
      : null,
    { name: product.title, url: canonicalUrl },
  ].filter(Boolean);
  const breadcrumbJsonLd = generateBreadcrumbSchema(breadcrumbs);

  const productJsonLd = generateProductSchema({
    id: product.id,
    slug: product.slug,
    name: product.title,
    description: product.description,
    images: productImages.fullSize,
    sku: product.reference,
    mpn: product.reference,
    brand: product.brand || 'Promocionales en Stock',
    manufacturer: product.manufacturer || 'Promocionales en Stock',
    price: product.price?.Precio,
    rating: product.rating || null,
    stock: product.stock || 0,
    category: productCategory ? productCategory.name : 'Productos Promocionales',
    variations: product.variations || [],
    minQuantity: product.cleanUnitsPerPackage || 1,
  });

  const faqs = [
    {
      question: '¿Cómo cuidar adecuadamente este producto?',
      answer:
        'Para cuidar este producto, asegúrate de seguir las instrucciones específicas de lavado y mantenimiento.',
    },
    {
      question: '¿Cuánto tiempo tarda en llegar mi pedido?',
      answer:
        'El tiempo de entrega varía según la ubicación, pero generalmente es entre 5 a 10 días hábiles.',
    },
    {
      question: '¿Puedo personalizar el producto?',
      answer: 'Sí, ofrecemos opciones de personalización. Por favor, contáctanos para más detalles.',
    },
    {
      question: '¿Qué métodos de pago aceptan?',
      answer:
        'Aceptamos tarjetas de crédito, débito y otros métodos de pago electrónico.',
    },
  ];

  const faqJsonLdBlock = generateFaqPageSchema(faqs);

  const jsonLdSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      generateWebsiteSchema(),
      generateOrganizationSchema(),
      breadcrumbJsonLd,
      productJsonLd,
      faqJsonLdBlock
    ]
  };

  const additionalMetaTags = [
    { name: 'robots', content: 'index,follow,max-image-preview:large' },
    { name: 'keywords', content: product.title },
    { name: 'author', content: 'Promocionales en Stock' },
    { name: 'geo.region', content: 'CO-DC' },
    { name: 'geo.placename', content: 'Bogotá' },
  ];

  return (
    <div className="ProductPage container">
      <Seo
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        prevPageUrl={null}
        nextPageUrl={null}
        ogImage={productImages.fullSize[0] || '/images/og-default.jpg'}
        ogType="product"
        jsonLd={jsonLdSchema}
        additionalMeta={additionalMetaTags}
        siteUrl={SITE_URL}
      />

      <div
        itemScope
        itemType="https://schema.org/Product"
        style={{ display: 'none' }}
        aria-hidden="true"
      >
        <meta itemProp="name" content={product.title} />
        <meta itemProp="description" content={pageDescription} />
        <meta itemProp="sku" content={product.reference} />
        <meta itemProp="url" content={`${SITE_URL}${canonicalUrl}`} />
      </div>

      <div className="row">
        {/* Galería */}
        <div className="ProductPage__Gallery col-12 col-lg-6">
          <ProductImageCarousel
            productReference={product.reference}
            thumbnails={productImages.thumbnails}
            fullSize={productImages.fullSize}
          />
        </div>

        <div className="ProductPage__Info col-12 col-lg-6">
          <nav className="ProductPage__breadcrumb" aria-label="Ubicación en el sitio">
            <Link to="/">Inicio</Link>
            <span className="ProductPage__breadcrumb-sep" aria-hidden="true">›</span>
            <Link to="/categorias">Categorías</Link>
            {productCategory?.slug && (
              <>
                <span className="ProductPage__breadcrumb-sep" aria-hidden="true">›</span>
                <Link to={`/categorias/${productCategory.slug}`}>{productCategory.name}</Link>
              </>
            )}
            {productSubcategory?.slug && productCategory?.slug && (
              <>
                <span className="ProductPage__breadcrumb-sep" aria-hidden="true">›</span>
                <Link
                  to={`/categorias/${productCategory.slug}/${productSubcategory.slug}`}
                >
                  {cleanSubcategoryName(productSubcategory.name)}
                </Link>
              </>
            )}
            <span className="ProductPage__breadcrumb-sep" aria-hidden="true">›</span>
            <span className="ProductPage__breadcrumb-current">{product.title}</span>
          </nav>

          <h1 className="ProductPage__title">{product.title}</h1>

          <div className="ProductPage__badges-row">
            {product.isNew && <span className="ProductPage__badge">NUEVO</span>}
            {categories.map((cat) =>
              cat.slug ? (
                <Link
                  key={`tax-cat-${cat.id}`}
                  className="ProductPage__taxonomy-tag ProductPage__taxonomy-tag--category"
                  to={`/categorias/${cat.slug}`}
                >
                  {cat.name}
                </Link>
              ) : null
            )}
            {subcategories.map((sub) => {
              const parent = categories.find((c) => c.id === sub.categoryId) || categories[0];
              if (!parent?.slug || !sub.slug) return null;
              return (
                <Link
                  key={`tax-sub-${sub.id}`}
                  className="ProductPage__taxonomy-tag ProductPage__taxonomy-tag--sub"
                  to={`/categorias/${parent.slug}/${sub.slug}`}
                >
                  {cleanSubcategoryName(sub.name)}
                </Link>
              );
            })}
            {categories.length === 0 && subcategories.length === 0 && (
              <span className="ProductPage__taxonomy-empty">Sin categoría asignada</span>
            )}
          </div>

          <span className="ProductPage__reference">{product.reference}</span>

          {isUnavailable && (
            <div className="ProductPage__unavailable" role="status">
              <strong>Producto no disponible.</strong> La bodega ya no ofrece esta referencia.
              Escríbenos o llámanos y te ayudamos a encontrar una alternativa.
            </div>
          )}

          {/* <div className="ProductPage__price">
            <span className="ProductPage__price--public">
              <strong>Precio público:</strong> {displayPublicPrice}
            </span>
            {isAuthenticated && product.warehousePrice && (
              <span className="ProductPage__price--warehouse">
                <strong>Precio bodega:</strong> {product.warehousePrice}
              </span>
            )}
          </div> */}

          <div className="ProductPage__details">
            <div className="ProductPage__description">
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>

            {upcomingArrivals.length > 0 && (
              <div className="ProductPage__arrivals">
                <h3 className="h6">Próximas llegadas</h3>
                <ul className="ProductPage__arrivals-list">
                  {upcomingArrivals.map((arrival) => (
                    <li key={arrival.fecha} className="ProductPage__arrivals-item">
                      <span className="ProductPage__arrivals-date">
                        {formatArrivalDate(arrival.fecha, 'long')}
                      </span>
                      <span className="ProductPage__arrivals-quantity">
                        {formatUnits(arrival.cantidad)} unidades
                      </span>
                    </li>
                  ))}
                </ul>
                {arrivalsByColorAvailable && (
                  <button type="button" className="ProductPage__arrivals-link" onClick={scrollToStock}>
                    Ver llegadas por color
                  </button>
                )}
              </div>
            )}

            {/* {product.warehouse !== 'En-stock' && (
              <div className="ProductPage__packinginfo">
                <ProductPackagingInfo
                  unitsPerPackage={product.cleanUnitsPerPackage}
                  packageDetails={product.cleanPackage}
                  warehouse={product.warehouse}
                />
              </div>
            )} */}

            {hasVariations && (
              <div className="ProductPage__variation-block">
                <h3 className="h6">Variaciones disponibles</h3>
                <VariationSelector
                  variations={product.variations}
                  selectedVariation={selectedVariation}
                  onSelectVariation={handleVariationSelect}
                />
                {variationError && (
                  <p className="ProductPage__variation-error">{variationError}</p>
                )}
              </div>
            )}

            <div className="ProductPage__quantity-selector">
              <QuantitySelector
                min={1}
                max={10000}
                onQuantityChange={handleQuantityChange}
              />
            </div>

            
            {/* {product.warehouse !== 'En-stock' && predictive && (
              <p className="ProductPage__predictive-quote">
                Este producto en esas cantidades tiene un valor aproximado entre{' '}
                <strong>{predictive.min}</strong> y <strong>{predictive.max}</strong>.{' '}
                <em>¡No te quedes con la duda: solicita tu cotización ahora y asegura el mejor precio!</em>
              </p>
            )} */}

            <div className="ProductPage__cta">
              <Button
                text="Solicitar una cotización"
                className="btn-primary"
                onClick={handleRequestQuotation}
                disabled={isUnavailable || (hasVariations && !selectedVariation)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="ProductPage__VariationsTable col-12" id="existencias">
          <ProductVariationsTable
            variations={product.variations}
            warehouse={product.warehouse}
            arrivals={upcomingArrivals}
          />
        </div>
      </div>

      {product.ficha_tecnica && (
        <div className="row">
          <div className="col-12">
            <ProductSpecs specs={product.ficha_tecnica} />
          </div>
        </div>
      )}

      <div className="row">
        <div className="ProductPage__FAQ col-12">
          <Faq faqs={faqs} />
        </div>
      </div>

      {/* Productos recomendados */}
      <div className="row">
        <ProductCarousel
          sectionTitle="Productos Recomendados"
          sectionSubtitle="Elige entre nuestra enorme colección personalizable de bolígrafos, vasos, botellas, tecnología y mucho más."
          endpoint="random-products"
        />
      </div>
    </div>
  );
};

export default ProductPage;
